import { TagGroup, Tag } from '@backstage/ui';
import { MarkdownContent } from '@backstage/core-components';
import { CardContent as UICardContent } from '@material-ui/core';
import {
  RiLink,
  RiApps2Fill,
  RiChat1Fill,
  RiCheckboxCircleFill,
  RiCloudFill,
  RiCodeFill,
  RiDashboard3Fill,
  RiDatabase2Fill,
  RiErrorWarningFill,
  RiExternalLinkFill,
  RiFileTextFill,
  RiGithubFill,
  RiGroupFill,
  RiMailFill,
  RiQuestionFill,
  RiSettings3Fill,
  RiToolsFill,
  RiUserFill,
} from '@remixicon/react';
import type { RemixiconComponentType } from '@remixicon/react';
import { useTheme } from '@mui/material/styles';
import { CardType, CardVariant } from '../../lib/types';
import { useCard } from '../../lib/context';

const ENTITY_LINK_ICON_MAP: Record<string, RemixiconComponentType> = {
  catalog: RiApps2Fill,
  chat: RiChat1Fill,
  check: RiCheckboxCircleFill,
  cloud: RiCloudFill,
  code: RiCodeFill,
  dashboard: RiDashboard3Fill,
  docs: RiFileTextFill,
  email: RiMailFill,
  error: RiErrorWarningFill,
  external: RiExternalLinkFill,
  github: RiGithubFill,
  group: RiGroupFill,
  help: RiQuestionFill,
  settings: RiSettings3Fill,
  storage: RiDatabase2Fill,
  tool: RiToolsFill,
  user: RiUserFill,
};

const Tags = () => {
  const theme = useTheme();

  const { type, entity, link, variant, themedStyles } = useCard();

  const cardTags = link?.tags || entity?.metadata?.tags || [];
  const hasTags = cardTags.length > 0;

  const isMinimalVariant = variant === CardVariant.minimal;
  const isLinkType = type === CardType.link;

  const uiTagItems = cardTags.map(tag => ({ id: tag, name: tag }));

  return (
    (!isMinimalVariant || isLinkType) &&
    hasTags && (
      <div style={{ margin: theme.spacing(1, 0, 1) }}>
        <TagGroup aria-label="Tag Group" items={uiTagItems}>
          {item => <Tag className={themedStyles.tag}>{item.name}</Tag>}
        </TagGroup>
      </div>
    )
  );
};

const Links = () => {
  const theme = useTheme();
  const { entity, variant, themedStyles } = useCard();

  const isMinimalVariant = variant === CardVariant.minimal;

  const hasLinks = (entity?.metadata?.links || []).length > 0;

  return (
    !isMinimalVariant &&
    hasLinks && (
      <div style={{ margin: theme.spacing(0, 0, 1) }}>
        <TagGroup aria-label="Link Tag Group">
          {(entity?.metadata?.links || []).map(link => {
            const IconComponent = link.icon
              ? ENTITY_LINK_ICON_MAP[link.icon.toLowerCase()] ?? RiLink
              : RiLink;

            return (
              <Tag
                key={link.url}
                icon={<IconComponent />}
                size="small"
                className={themedStyles.link}
                href={link.url}
                target="_blank"
                rel="noopener"
              >
                {link.title || link.url}
              </Tag>
            );
          })}
        </TagGroup>
      </div>
    )
  );
};

export const CardContent = () => {
  const {
    type,
    entity,
    description,
    maxDescriptionLength,
    themedStyles,
    variant,
    withoutTags,
    withoutLinks,
  } = useCard();

  // manually injected description overrides
  let contentToRender = description ?? entity?.metadata?.description;
  // don't show description if it's the same as the name
  contentToRender =
    contentToRender === entity?.metadata?.name ? '' : contentToRender;
  // shorten description for minimal variant
  contentToRender =
    contentToRender &&
    maxDescriptionLength &&
    contentToRender.length > maxDescriptionLength
      ? `${contentToRender.substring(0, maxDescriptionLength)}...`
      : contentToRender;
  contentToRender = type === CardType.link ? undefined : contentToRender;

  const shouldApplyStyles = entity || variant === CardVariant.minimal;
  const classes = shouldApplyStyles ? themedStyles?.content : undefined;

  return (
    <UICardContent className={classes}>
      {contentToRender && <MarkdownContent content={contentToRender} />}
      {!withoutTags && <Tags />}
      {!withoutLinks && <Links />}
    </UICardContent>
  );
};
